/**
 * Feature Flags Initialization Plugin (client-only)
 *
 * Drives when the feature flags store loads/clears, mirroring the auth lifecycle:
 * - Initial load: hydrate from localStorage for instant UI, then refresh from the
 *   API if already authenticated on boot.
 * - Refetch on the 3 events of FR-013: login + company switch are covered by the
 *   watcher on the active company id; a token refresh that changes claims also
 *   updates the auth store and therefore re-triggers the watcher.
 * - Logout: clear the store (and its persisted copy).
 *
 * See spec 030 — internal-contracts.md § 4.3.
 */

import { watch } from 'vue'
import { useFeatureFlagsStore } from '~/stores/featureFlags'

export default defineNuxtPlugin({
  name: 'feature-flags-init',
  dependsOn: ['pinia'],

  setup(nuxtApp) {
    // Only run on client side
    if (!import.meta.client) {
      return
    }

    const pinia = nuxtApp.$pinia
    const authStore = useAuthStore(pinia)
    const featureFlagsStore = useFeatureFlagsStore(pinia)

    // Instant hydration from the persisted copy (no network), then refresh —
    // but ONLY when there is an authenticated session. If we boot logged out
    // (login page, failed session restore, etc.) we must NOT surface flags from
    // a previous session: clear any persisted copy and keep the store empty
    // (fail-closed). The logout watcher below is not `{ immediate: true }`, so
    // this is the path that handles the "boot while logged out" case.
    if (authStore.isAuthenticated) {
      featureFlagsStore.hydrateFromStorage()
      featureFlagsStore.load().catch((error) => {
        console.error('Failed to load feature flags on boot:', error)
      })
    } else {
      featureFlagsStore.clearState()
    }

    // Refetch when the user logs in or switches company (and on claim-changing
    // token refreshes, which update the auth store too) — FR-013.
    watch(
      () => authStore.user?.workspace?.company?.id ?? null,
      (companyId, previousCompanyId) => {
        if (companyId === previousCompanyId) {
          return
        }
        if (companyId) {
          featureFlagsStore.load().catch((error) => {
            console.error('Failed to refresh feature flags:', error)
          })
        }
      }
    )

    // Clear the store when the user logs out.
    watch(
      () => authStore.isAuthenticated,
      (isAuthenticated) => {
        if (!isAuthenticated) {
          featureFlagsStore.clearState()
        }
      }
    )
  },
})
